import { useState, useEffect } from "react";
import axios from "axios";
import {
  FiPackage,
  FiShoppingBag,
  FiAlertCircle,
  FiCheckCircle,
  FiCalendar,
  FiClock,
} from "react-icons/fi";
import { BsBox, BsTruck, BsCreditCard2Back } from "react-icons/bs";
import { IoReceiptOutline } from "react-icons/io5";
import { MdOutlineLoop } from "react-icons/md";
import { useNavigate } from "react-router-dom";
import OrderSkeletonLoader from "./OrderSkeletonLoader";

const AllOrders = () => {
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState("all");
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOrders = async () => {
      try {
        setLoading(true);
        // Get user data from localStorage
        const userData = JSON.parse(
          localStorage.getItem("BricksNbar-Users-Data") || "{}"
        );
        const userId = userData?._id;

        if (!userId) {
          setError("User not logged in");
          setLoading(false);
          return;
        }

        const response = await axios.get(
          `${process.env.REACT_APP_API_URL}/orders/get-user-orders/${userId}`
        );

        // Based on the data structure from the pasted JSON, the response data itself is the array
        if (response.data && Array.isArray(response.data)) {
          setOrders(response.data.reverse());
        } else if (response.data && Array.isArray(response.data.orders)) {
          setOrders(response.data.orders);
        } else {
          setOrders([]);
        }
        setLoading(false);
      } catch (err) {
        console.error("Error fetching orders:", err);
        setError("Failed to fetch orders. Please try again later.");
        setLoading(false);
      }
    };

    fetchOrders();
  }, []);

  // Function to format date
  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", {
      year: "numeric",
      month: "short",
      day: "numeric",
    });
  };

  // Status badge component with appropriate colors
  const StatusBadge = ({ status }) => {
    let bgColor = "bg-gray-200";
    let icon = null;

    switch (status) {
      case "Delivered":
        bgColor = "bg-green-500 text-white";
        icon = <FiCheckCircle className="mr-1" />;
        break;
      case "Shipped":
        bgColor = "bg-gray-500 text-white";
        icon = <BsTruck className="mr-1" />;
        break;
      case "Pending":
        bgColor = "bg-yellow-500 text-white";
        icon = <FiClock className="mr-1" />;
        break;
      case "Cancelled":
        bgColor = "bg-red-500 text-white";
        icon = <FiAlertCircle className="mr-1" />;
        break;
      default:
        bgColor = "bg-gray-500 text-white";
        icon = <FiPackage className="mr-1" />;
    }

    return (
      <span
        className={`px-3 py-1 rounded-full text-xs font-medium ${bgColor} flex items-center shadow-sm`}
      >
        {icon}
        {status}
      </span>
    );
  };

  // Exchange request badge
  const ExchangeBadge = ({ exchangeRequest }) => {
    if (!exchangeRequest || !exchangeRequest.isRequested) return null;

    let bgColor = "bg-gray-500 text-white";
    let icon = <MdOutlineLoop className="mr-1" />;

    switch (exchangeRequest.status) {
      case "Completed":
        bgColor = "bg-green-500 text-white";
        icon = <FiCheckCircle className="mr-1" />;
        break;
      case "Pending":
        bgColor = "bg-yellow-500 text-white";
        icon = <FiClock className="mr-1" />;
        break;
      case "Rejected":
        bgColor = "bg-red-500 text-white";
        icon = <FiAlertCircle className="mr-1" />;
        break;
      default:
        bgColor = "bg-gray-500 text-white";
    }

    return (
      <span
        className={`ml-2 px-3 py-1 rounded-full text-xs font-medium ${bgColor} flex items-center shadow-sm`}
      >
        {icon}
        Exchange: {exchangeRequest.status}
      </span>
    );
  };

  const filteredOrders = orders.filter((order) => {
    if (activeTab === "all") return true;
    if (activeTab === "delivered") return order.status === "Delivered";
    if (activeTab === "shipped") return order.status === "Shipped";
    if (activeTab === "cancelled") return order.status === "Cancelled";
    if (activeTab === "pending") return order.status === "Pending";
    if (activeTab === "exchanged")
      return order.exchangeRequest && order.exchangeRequest.isRequested;
    return true;
  });

  const handleOrderClick = (orderId) => {
    navigate(`/orders/${orderId}`);
  };

  // Loading spinner
  if (loading) {
    return <OrderSkeletonLoader />;
  }

  // Error state
  if (error) {
    return (
      <div className="bg-red-50 p-6 rounded-lg shadow-md max-w-lg mx-auto mt-10">
        <div className="flex items-center">
          <div className="flex-shrink-0 bg-red-100 rounded-full p-3">
            <FiAlertCircle className="h-6 w-6 text-red-500" />
          </div>
          <div className="ml-4">
            <h3 className="text-lg font-medium text-red-800">
              Unable to load orders
            </h3>
            <div className="mt-2 text-sm text-red-700">
              <p>{error}</p>
            </div>
            <div className="mt-4">
              <button
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-red-600 hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-red-500"
                onClick={() => window.location.reload()}
              >
                Try Again
              </button>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Empty state
  if (orders.length === 0) {
    return (
      <div className="text-center py-16 bg-white rounded-lg shadow-md w-full mx-auto mt-10">
        <div className="mx-auto w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center">
          <BsBox className="h-12 w-12 text-gray-400" />
        </div>
        <h3 className="mt-6 text-lg font-medium text-gray-900">
          No orders found
        </h3>
        <p className="mt-2 text-base text-gray-500 max-w-sm mx-auto">
          Looks like you haven't placed any orders yet. Start shopping to create
          your first order.
        </p>
        <div className="mt-6">
          <button
            className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
            onClick={() => {
              navigate("/products");
            }}
          >
            <FiShoppingBag className="mr-2" />
            Browse Products
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-gray-50 min-h-screen py-8">
      <div className="container mx-auto px-4 w-full">
        {/* Header */}
        <div className="bg-white rounded-lg shadow-md p-6 mb-6">
          <h1 className="text-2xl font-bold text-gray-800 flex items-center mb-2">
            <FiShoppingBag className="mr-3 text-orange-500" />
            My Orders
          </h1>
          <p className="text-gray-600">Track and manage your order history</p>

          {/* Status filter tabs */}
          <div className="mt-6 border-b border-gray-200">
            <nav
              className="flex space-x-4 overflow-x-auto pb-1"
              aria-label="Tabs"
            >
              <button
                onClick={() => setActiveTab("all")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "all"
                    ? "bg-orange-100 text-orange-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                All Orders
              </button>

              <button
                onClick={() => setActiveTab("pending")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "pending"
                    ? "bg-blue-100 text-blue-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiClock className="inline mr-1" />
                Pending
              </button>
              <button
                onClick={() => setActiveTab("shipped")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "shipped"
                    ? "bg-gray-100 text-gray-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <BsTruck className="inline mr-1" />
                Shipped
              </button>
              <button
                onClick={() => setActiveTab("delivered")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "delivered"
                    ? "bg-green-100 text-green-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiCheckCircle className="inline mr-1" />
                Delivered
              </button>
              <button
                onClick={() => setActiveTab("cancelled")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "cancelled"
                    ? "bg-red-100 text-red-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <FiAlertCircle className="inline mr-1" />
                Cancelled
              </button>
              <button
                onClick={() => setActiveTab("exchanged")}
                className={`px-3 py-2 text-sm font-medium rounded-md ${
                  activeTab === "exchanged"
                    ? "bg-yellow-100 text-yellow-700"
                    : "text-gray-500 hover:text-gray-700"
                }`}
              >
                <MdOutlineLoop className="inline mr-1" />
                Exchanges
              </button>
            </nav>
          </div>
        </div>

        {/* Order count summary */}
        <div className="mb-6">
          <p className="text-sm text-gray-600">
            Showing {filteredOrders.length}{" "}
            {filteredOrders.length === 1 ? "order" : "orders"}
          </p>
        </div>

        {/* Orders list */}
        <div className="space-y-6">
          {filteredOrders.length === 0 ? (
            <div className="bg-white rounded-lg shadow-md p-8 text-center">
              <p className="text-gray-500">
                No {activeTab !== "all" ? activeTab : ""} orders found
              </p>
            </div>
          ) : (
            filteredOrders.map((order) => (
              <div
                key={order._id}
                onClick={() => handleOrderClick(order._id)}
                className="bg-white rounded-lg shadow-md overflow-hidden transform transition duration-200 hover:shadow-lg"
              >
                {/* Order header */}
                <div className="p-6 border-b bg-gray-50">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                    <div className="mb-4 md:mb-0">
                      <div className="flex items-center mb-1">
                        <IoReceiptOutline className="text-gray-600 mr-2" />
                        <span className="text-xs text-gray-500">ORDER</span>
                        <span className="ml-2 text-sm font-bold text-gray-800">
                          #{order._id.slice(-8).toUpperCase()}
                        </span>
                      </div>
                      <div className="flex items-center">
                        <FiCalendar className="text-gray-600 mr-2" />
                        <span className="text-sm text-gray-600">
                          {formatDate(order.createdAt)}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-wrap gap-2">
                      <StatusBadge status={order.status} />
                      <ExchangeBadge exchangeRequest={order.exchangeRequest} />
                    </div>
                  </div>
                </div>

                {/* Order items - Show only the first product */}
                <div className="p-6">
                  {order.products.length > 0 && (
                    <div className="flex flex-col sm:flex-row py-4 hover:bg-gray-50 transition duration-150">
                      <div className="h-24 w-24 sm:h-20 sm:w-20 flex-shrink-0 overflow-hidden rounded-lg border border-gray-200 mb-4 sm:mb-0">
                        <img
                          src={order.products[0].product.images[0]}
                          alt={order.products[0].product.name}
                          className="h-full w-full object-cover object-center"
                        />
                      </div>
                      <div className="sm:ml-6 flex flex-1 flex-col">
                        <div className="flex justify-between">
                          <div>
                            <h3 className="text-base font-medium text-gray-900">
                              {order.products[0].product.name}
                            </h3>
                            <p className="mt-1 text-sm text-gray-500">
                              Brand: {order.products[0].product.brand}
                            </p>
                            <div className="mt-1 text-sm text-gray-500">
                              <span className="inline-flex items-center px-2 py-0.5 rounded bg-gray-100 text-gray-800 mr-2">
                                Qty: {order.products[0].quantity}
                              </span>
                              {order.products.length > 1 && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded bg-orange-100 text-orange-800">
                                  +{order.products.length - 1} more item(s)
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="text-right">
                            <p className="text-lg font-medium text-gray-900">
                              ₹{order.products[0].price.toLocaleString()}
                            </p>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>

                {/* Order footer */}
                <div className="bg-gray-50 p-6">
                  <div className="flex flex-col md:flex-row md:justify-between md:items-center">
                    <div className="mb-4 md:mb-0">
                      {order.pod_img_url && (
                        <div className="flex items-center">
                          <BsTruck className="text-gray-600 mr-2" />
                          <span className="text-sm font-medium text-gray-700">
                            Delivery Proof:
                          </span>
                          <a
                            href={order.pod_img_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="ml-2 text-sm text-blue-600 hover:text-blue-800 underline"
                          >
                            View Image
                          </a>
                        </div>
                      )}
                    </div>
                    <div className="flex items-center bg-orange-50 px-4 py-3 rounded-lg">
                      <BsCreditCard2Back className="text-orange-600 mr-2" />
                      <span className="text-sm text-gray-700 mr-2">Total:</span>
                      <span className="text-lg font-bold text-gray-900">
                        ₹{order.totalAmount.toLocaleString()}
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            ))
          )}
        </div>
      </div>
    </div>
  );
};

export default AllOrders;
